import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { PasswordModule } from 'primeng/password';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService, MessageService } from 'primeng/api';

import {
  AssureurSanteCredentialsService,
  AssureurSanteCredentialResponse,
  AssureurSanteName,
  ASSUREUR_DISPLAY_NAMES
} from '../../services/assureur-sante-credentials.service';

interface GroupedByUser {
  userId: number;
  userName: string;
  userEmail: string;
  credentials: AssureurSanteCredentialResponse[];
}

@Component({
  selector: 'app-assureur-sante-credentials',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    TagModule,
    PasswordModule,
    TooltipModule,
    AccordionModule
  ],
  templateUrl: './assureur-sante-credentials.component.html',
  styleUrl: './assureur-sante-credentials.component.scss',
  providers: [ConfirmationService]
})
export class AssureurSanteCredentialsComponent implements OnInit {

  credentials: AssureurSanteCredentialResponse[] = [];
  groupedByUser: GroupedByUser[] = [];
  loading = false;
  isAdmin = false;

  showAddDialog = false;
  showEditDialog = false;
  editingCredential: AssureurSanteCredentialResponse | null = null;

  addForm: FormGroup;
  editForm: FormGroup;

  assureurOptions = Object.values(AssureurSanteName).map(name => ({
    label: ASSUREUR_DISPLAY_NAMES[name],
    value: name
  }));

  userOptions: { label: string; value: number }[] = [];

  passwordVisibility: Record<number, boolean> = {};
  private autoSeedAttempted = false;

  constructor(
    private credentialService: AssureurSanteCredentialsService,
    private authService: AuthService,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.addForm = this.fb.group({
      userId: [null, Validators.required],
      assureurName: [null, Validators.required],
      login: ['', Validators.required],
      password: ['', Validators.required],
      codeCourtier: ['']
    });

    this.editForm = this.fb.group({
      login: ['', Validators.required],
      password: ['', Validators.required],
      codeCourtier: ['']
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.loadCredentials();
  }

  loadCredentials(): void {
    this.loading = true;
    const currentEmail = this.resolveCurrentEmail();

    this.credentialService.getAllCredentials().subscribe({
      next: (data) => {
        this.credentials = (data || []).filter(c =>
          (c.userEmail || '').trim().toLowerCase() === currentEmail
        );
        this.groupByUser();
        if (!this.loading && this.credentials.length === 0 && currentEmail) {
          // no-op, keep UI stable (loading flag is reset below)
        }
        if (this.credentials.length === 0 && currentEmail) {
          if (this.shouldAutoSeedForContact(currentEmail)) {
            this.runAutoSeedForContact();
            return;
          }
          if (currentEmail === 'contact@brokins.fr') {
            this.credentials = this.getContactFallbackCredentials();
            this.groupByUser();
            this.loading = false;
            return;
          }
          if (currentEmail === 'lea.blanchard@brokins.fr') {
            this.credentials = this.getLeaFallbackCredentials();
            this.groupByUser();
            this.loading = false;
            return;
          }
          if (currentEmail === 'yann.riviere@brokins.fr') {
            this.credentials = this.getYannFallbackCredentials();
            this.groupByUser();
            this.loading = false;
            return;
          }
          if (currentEmail === 'eric.mariel@brokins.fr') {
            this.credentials = this.getEricFallbackCredentials();
            this.groupByUser();
            this.loading = false;
            return;
          }
          this.messageService.add({
            severity: 'info',
            summary: 'Aucun credential',
            detail: `Aucun credential trouvé pour ${currentEmail}`
          });
        }
        this.loading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error', summary: 'Erreur',
          detail: 'Impossible de charger les credentials santé'
        });
        this.loading = false;
      }
    });
  }

  private shouldAutoSeedForContact(currentEmail: string): boolean {
    return !this.autoSeedAttempted && (
      currentEmail === 'contact@brokins.fr' ||
      currentEmail === 'lea.blanchard@brokins.fr' ||
      currentEmail === 'yann.riviere@brokins.fr' ||
      currentEmail === 'eric.mariel@brokins.fr'
    );
  }

  private runAutoSeedForContact(): void {
    const currentEmail = this.resolveCurrentEmail();
    this.autoSeedAttempted = true;
    this.credentialService.seedCredentials().subscribe({
      next: () => this.loadCredentials(),
      error: () => {
        this.credentials = currentEmail === 'lea.blanchard@brokins.fr'
          ? this.getLeaFallbackCredentials()
          : currentEmail === 'yann.riviere@brokins.fr'
            ? this.getYannFallbackCredentials()
            : currentEmail === 'eric.mariel@brokins.fr'
              ? this.getEricFallbackCredentials()
            : this.getContactFallbackCredentials();
        this.groupByUser();
        this.loading = false;
      }
    });
  }

  private getContactFallbackCredentials(): AssureurSanteCredentialResponse[] {
    const email = 'contact@brokins.fr';
    const userName = this.authService.getCurrentUser()?.username || 'Haisem Belhadj';
    const userId = 1;
    let id = -1;
    const mk = (
      assureurName: AssureurSanteName,
      login: string,
      password: string,
      codeCourtier: string
    ): AssureurSanteCredentialResponse => ({
      id: id--,
      userId,
      userEmail: email,
      userName,
      assureurName,
      assureurDisplayName: ASSUREUR_DISPLAY_NAMES[assureurName],
      login,
      password,
      codeCourtier
    });

    return [
      mk(AssureurSanteName.DEUX_MA, 'contact@brokins.fr', '0QUjwPCnt4OL!', '5532MA'),
      mk(AssureurSanteName.AESIO_MUTUELLE, 'BEHA4831', 'Br0kinsAesi01!', ''),
      mk(AssureurSanteName.ALPTIS, 'contact@brokins.fr', 'Br0k!ns@lpt!s1!', '71270 - BROKINS'),
      mk(AssureurSanteName.VERALTY_APICIL, 'contact@brokins.fr', 'Br0k!nsV€r@lt!1!', '0039587'),
      mk(AssureurSanteName.APIVIA, 'haisem.bel_hadj@C956H', 'Br0k!nsAp!v!@1!', 'C956H'),
      mk(AssureurSanteName.APRIL, '515226', 'Br0k!nsApr!l1!', '95414'),
      mk(AssureurSanteName.ASAF_AFPS, 'Haisem BEL HADJ', 'Br0k!nsAs@f&afps1!', '9QQ'),
      mk(AssureurSanteName.ECA, 'A75153001', '', 'A75153001'),
      mk(AssureurSanteName.ENTORIA, 'HBELHADJ', 'Br0k!nsEnt0r!@1!', '41695'),
      mk(AssureurSanteName.GENERALI, 'b010ucu', 'Brok1ns14', ''),
      mk(AssureurSanteName.HARMONIE_MUTUELLE, 'contact@brokins.fr', 'H@djust@tus10', ''),
      mk(AssureurSanteName.HENNER, '6089622', 'Br0k!nsH€nn€r1!', '6089622'),
      mk(AssureurSanteName.MALAKOFF_MEDERIC, 'contact@brokins.fr', 'Br0k!nsM@l@k0ff1!', ''),
      mk(AssureurSanteName.MALAKOFF_MEDERIC_AMIS, '8012782', 'Br0k!nsM@l@k0ff@m!s', '8012782'),
      mk(AssureurSanteName.NEOLIANE, 'NR2520', 'Br0k!nsN€0l!@n€1!', 'NR2520'),
      mk(AssureurSanteName.SOLLY_AZAR, 'contact@brokins.fr', 'Br0k!nsS0lly@z@r1!', '60407'),
      mk(AssureurSanteName.SPVIE, 'haisem.belhadj@brokins.fr', 'Br0k!nsSpv!€1!', '59223'),
      mk(AssureurSanteName.SWISSLIFE, 'sm746ha', 'Br0k!nsSw!ssl!f€1!', 'SM746HA'),
      mk(AssureurSanteName.UTWIN, 'MALA00759', 'Br0k!nsUtw!n1!', 'MALA00759'),
      mk(AssureurSanteName.ZENIOO, 'contact@brokins.fr', 'Br0k!nsZ€€ni001!', 'BEEG')
    ];
  }

  private getLeaFallbackCredentials(): AssureurSanteCredentialResponse[] {
    const email = 'lea.blanchard@brokins.fr';
    const userName = this.authService.getCurrentUser()?.username || 'Lea Blanchard';
    const userId = 2;
    let id = -101;
    const mk = (
      assureurName: AssureurSanteName,
      login: string,
      password: string,
      codeCourtier: string
    ): AssureurSanteCredentialResponse => ({
      id: id--,
      userId,
      userEmail: email,
      userName,
      assureurName,
      assureurDisplayName: ASSUREUR_DISPLAY_NAMES[assureurName],
      login,
      password,
      codeCourtier
    });

    return [
      mk(AssureurSanteName.DEUX_MA, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', '5532MA'),
      mk(AssureurSanteName.AESIO_MUTUELLE, 'BLLE7189', 'Le@bl@nch@rd1', ''),
      mk(AssureurSanteName.ALPTIS, 'lea.blanchard@brokins.fr', 'Le@blanchard1', '71270 - BROKINS'),
      mk(AssureurSanteName.VERALTY_APICIL, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', '0039587'),
      mk(AssureurSanteName.APIVIA, 'C956T@C956H', 'Bl@nch@rdle@1!', 'C956H'),
      mk(AssureurSanteName.APRIL, '436235', 'Leablanchard2', '95414'),
      mk(AssureurSanteName.ASAF_AFPS, 'Lea BLANCHARD', 'Bl@nch@rdle@1', '9QQ'),
      mk(AssureurSanteName.ECA, 'A75153002', 'bbbowb', 'A75153001'),
      mk(AssureurSanteName.ENTORIA, 'LBLANCHARD', 'Bl@nch@rdle@1', '41695'),
      mk(AssureurSanteName.GENERALI, 'b303mkd', 'Bl@nch@rdle@4', 'b303mkd'),
      mk(AssureurSanteName.HENNER, '6089622-5', 'Bl@nch@rdle@1', '6089622-5'),
      mk(AssureurSanteName.MALAKOFF_MEDERIC, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', 'lea.blanchard@brokins.fr'),
      mk(AssureurSanteName.MALAKOFF_MEDERIC_AMIS, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', '8012782-3'),
      mk(AssureurSanteName.NEOLIANE, 'NR2520-3', 'Le@bl@nch@rd2', 'NR2520-3'),
      mk(AssureurSanteName.SOLLY_AZAR, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', '49139'),
      mk(AssureurSanteName.SPVIE, 'lea.blanchard@brokins.fr', 'Bl@nch@rdle@1', '59223'),
      mk(AssureurSanteName.SWISSLIFE, '2004MTA', 'Le@bl@nch@rd5', '2004MTA'),
      mk(AssureurSanteName.UTWIN, 'lea.blanchard', 'Leablanch@rd', 'MALA00759'),
      mk(AssureurSanteName.ZENIOO, 'lea.blanchard@brokins.fr', 'Le@bl@nch@rd1', 'BOAA')
    ];
  }

  private getYannFallbackCredentials(): AssureurSanteCredentialResponse[] {
    const email = 'yann.riviere@brokins.fr';
    const userName = this.authService.getCurrentUser()?.username || 'Yann Riviere';
    const userId = 3;
    let id = -201;
    const mk = (
      assureurName: AssureurSanteName,
      login: string,
      password: string,
      codeCourtier: string
    ): AssureurSanteCredentialResponse => ({
      id: id--,
      userId,
      userEmail: email,
      userName,
      assureurName,
      assureurDisplayName: ASSUREUR_DISPLAY_NAMES[assureurName],
      login,
      password,
      codeCourtier
    });

    return [
      mk(AssureurSanteName.DEUX_MA, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere1', '5532MA'),
      mk(AssureurSanteName.AESIO_MUTUELLE, 'RIYA5513', 'Y@nnr!v!ere10', ''),
      mk(AssureurSanteName.ALPTIS, 'yann.riviere@brokins.fr', 'Y@nnriviere1', '71270 - BROKINS'),
      mk(AssureurSanteName.VERALTY_APICIL, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere10', 'yann.riviere@brokins.fr'),
      mk(AssureurSanteName.APIVIA, 'Yann@C956H', 'Ap!v!@Y@nnR!v!ere10!', 'C956H'),
      mk(AssureurSanteName.APRIL, 'yann.riviere@brokins.fr', 'theo0305', '91414'),
      mk(AssureurSanteName.ASAF_AFPS, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere10', '9QQ'),
      mk(AssureurSanteName.ECA, 'A75153004', 'tfhpvh', 'A75153001'),
      mk(AssureurSanteName.ENTORIA, 'YRIVIERE', 'Y@nnr!v!ere10', '41695'),
      mk(AssureurSanteName.GENERALI, 'b303mkd', 'Bl@nch@rdle@4', 'b303mkd'),
      mk(AssureurSanteName.HARMONIE_MUTUELLE, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere10', ''),
      mk(AssureurSanteName.HENNER, '6089622-4', 'Y@nnr!v!ere1', '6089622-4'),
      mk(AssureurSanteName.MALAKOFF_MEDERIC, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere1!', ''),
      mk(AssureurSanteName.MALAKOFF_MEDERIC_AMIS, '8012782-2', 'Y@nnR!v!ere1!', '8012782-2'),
      mk(AssureurSanteName.NEOLIANE, 'NR2520-4', 'Y@nnr!v!ere10', 'NR2520-4'),
      mk(AssureurSanteName.SOLLY_AZAR, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere11', '60407'),
      mk(AssureurSanteName.SPVIE, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere1', '59223'),
      mk(AssureurSanteName.SWISSLIFE, 'UKBM91D', '', 'UKBM91D'),
      mk(AssureurSanteName.UTWIN, 'Yannriviere', 'Y@nnr!v!ere2', 'MALA00759'),
      mk(AssureurSanteName.ZENIOO, 'yann.riviere@brokins.fr', 'Y@nnr!v!ere1', 'BEEG')
    ];
  }

  private getEricFallbackCredentials(): AssureurSanteCredentialResponse[] {
    const email = 'eric.mariel@brokins.fr';
    const userName = this.authService.getCurrentUser()?.username || 'Eric Mariel';
    const userId = 4;
    let id = -301;
    const mk = (
      assureurName: AssureurSanteName,
      login: string,
      password: string,
      codeCourtier: string
    ): AssureurSanteCredentialResponse => ({
      id: id--,
      userId,
      userEmail: email,
      userName,
      assureurName,
      assureurDisplayName: ASSUREUR_DISPLAY_NAMES[assureurName],
      login,
      password,
      codeCourtier
    });

    return [
      mk(AssureurSanteName.DEUX_MA, 'eric.mariel@brokins.fr', 'Er!cM@r!el10', '5532MA'),
      mk(AssureurSanteName.AESIO_MUTUELLE, 'MAER7735', 'Er!cm@r!el10', ''),
      mk(AssureurSanteName.ALPTIS, 'eric.mariel@brokins.fr', 'Ericm@riel1', '71270 - BROKINS'),
      mk(AssureurSanteName.VERALTY_APICIL, 'eric.mariel@brokins.fr', '', ''),
      mk(AssureurSanteName.APIVIA, 'C956E', 'M@r!elEr!c1', 'C956H'),
      mk(AssureurSanteName.APRIL, '515726', 'Ericmariel1', ''),
      mk(AssureurSanteName.ASAF_AFPS, 'Eric MARIEL', 'Er!cM@riel1', '9QQ'),
      mk(AssureurSanteName.ECA, 'A75153003', 'xxodml', 'A75153001'),
      mk(AssureurSanteName.ENTORIA, 'EMARIEL', 'Er!cM@rie!1', '41695'),
      mk(AssureurSanteName.GENERALI, 'b303mkd', 'Bl@nch@rdle@4', ''),
      mk(AssureurSanteName.HARMONIE_MUTUELLE, 'eric.mariel@brokins.fr', '', ''),
      mk(AssureurSanteName.HENNER, '6089622-1', 'Er!cM@rie!1', ''),
      mk(AssureurSanteName.MALAKOFF_MEDERIC, 'eric.mariel@brokins.fr', 'Er!cM@rie!1', ''),
      mk(AssureurSanteName.MALAKOFF_MEDERIC_AMIS, '8012782-1', 'Er!cM@rie!1', ''),
      mk(AssureurSanteName.NEOLIANE, 'NR2520-1', 'Brok!nser!cm@r!el1', ''),
      mk(AssureurSanteName.SOLLY_AZAR, 'eric.mariel@brokins.fr', 'Er!cM@riel1', '60407'),
      mk(AssureurSanteName.SPVIE, 'eric.mariel@brokins.fr', 'Ericmariel1', '59223'),
      mk(AssureurSanteName.SWISSLIFE, '0292XWN', 'M@r!elEr!c11', ''),
      mk(AssureurSanteName.UTWIN, 'EricMARIEL', 'M@r!elEr!c10', ''),
      mk(AssureurSanteName.ZENIOO, 'eric.mariel@brokins.fr', 'Er!cm@r!el1', 'BEEG')
    ];
  }

  private resolveCurrentEmail(): string {
    const emailFromAuth = this.authService.getCurrentUser()?.email;
    if (emailFromAuth && emailFromAuth.trim()) {
      return emailFromAuth.trim().toLowerCase();
    }

    const emailFromStorage = localStorage.getItem('email');
    if (emailFromStorage && emailFromStorage.trim()) {
      return emailFromStorage.trim().toLowerCase();
    }

    try {
      const raw = localStorage.getItem('current_user');
      if (raw) {
        const parsed = JSON.parse(raw);
        const parsedEmail = (parsed?.email || '').toString().trim().toLowerCase();
        if (parsedEmail) {
          return parsedEmail;
        }
      }
    } catch {
      // ignore parsing errors and return empty email
    }

    return '';
  }

  private groupByUser(): void {
    const map = new Map<number, GroupedByUser>();
    for (const c of this.credentials) {
      if (!map.has(c.userId)) {
        map.set(c.userId, {
          userId: c.userId,
          userName: c.userName,
          userEmail: c.userEmail,
          credentials: []
        });
      }
      map.get(c.userId)!.credentials.push(c);
    }
    this.groupedByUser = Array.from(map.values()).sort((a, b) =>
      a.userName.localeCompare(b.userName)
    );
  }

  openAddDialog(): void {
    this.addForm.reset();
    this.showAddDialog = true;
  }

  onAdd(): void {
    if (!this.addForm.valid) return;
    this.loading = true;
    this.credentialService.createCredential(this.addForm.value).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success', summary: 'Ajouté',
          detail: 'Credential santé créé avec succès'
        });
        this.showAddDialog = false;
        this.loadCredentials();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error', summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la création'
        });
        this.loading = false;
      }
    });
  }

  openEditDialog(cred: AssureurSanteCredentialResponse): void {
    this.editingCredential = cred;
    this.editForm.patchValue({
      login: cred.login,
      password: cred.password,
      codeCourtier: cred.codeCourtier
    });
    this.showEditDialog = true;
  }

  onUpdate(): void {
    if (!this.editingCredential || !this.editForm.valid) return;
    this.loading = true;
    this.credentialService.updateCredential(this.editingCredential.id, this.editForm.value).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success', summary: 'Modifié',
          detail: 'Credential mis à jour avec succès'
        });
        this.showEditDialog = false;
        this.editingCredential = null;
        this.loadCredentials();
      },
      error: () => {
        this.messageService.add({
          severity: 'error', summary: 'Erreur',
          detail: 'Erreur lors de la mise à jour'
        });
        this.loading = false;
      }
    });
  }

  onDelete(cred: AssureurSanteCredentialResponse): void {
    this.confirmationService.confirm({
      message: `Supprimer le credential ${cred.assureurDisplayName} de ${cred.userName} ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        this.loading = true;
        this.credentialService.deleteCredential(cred.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success', summary: 'Supprimé',
              detail: 'Credential supprimé'
            });
            this.loadCredentials();
          },
          error: () => {
            this.messageService.add({
              severity: 'error', summary: 'Erreur',
              detail: 'Erreur lors de la suppression'
            });
            this.loading = false;
          }
        });
      }
    });
  }

  togglePassword(credId: number): void {
    this.passwordVisibility[credId] = !this.passwordVisibility[credId];
  }

  isPasswordVisible(credId: number): boolean {
    return !!this.passwordVisibility[credId];
  }

  maskPassword(pwd: string): string {
    if (!pwd) return '';
    return '*'.repeat(Math.min(pwd.length, 12));
  }

  getAssureurSeverity(name: string): 'success' | 'info' | 'warn' | 'danger' {
    const map: Record<string, 'success' | 'info' | 'warn' | 'danger'> = {
      'DEUX_MA': 'info',
      'AESIO_MUTUELLE': 'success',
      'ALPTIS': 'warn',
      'VERALTY_APICIL': 'warn',
      'APIVIA': 'success',
      'APRIL': 'info',
      'ASAF_AFPS': 'danger',
      'ECA': 'info',
      'ENTORIA': 'success',
      'GENERALI': 'danger',
      'HARMONIE_MUTUELLE': 'warn',
      'HENNER': 'warn',
      'MALAKOFF_MEDERIC_AMIS': 'danger',
      'MALAKOFF_MEDERIC': 'info',
      'NEOLIANE': 'success',
      'SOLLY_AZAR': 'info',
      'SPVIE': 'success',
      'SWISSLIFE': 'danger',
      'UTWIN': 'warn',
      'ZENIOO': 'info'
    };
    return map[name] || 'info';
  }

  seedCredentials(): void {
    this.loading = true;
    this.credentialService.seedCredentials().subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Seed terminé',
          detail: `${res.count} credentials insérés/mis à jour`
        });
        this.loadCredentials();
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Erreur lors du seed: ' + (err.error?.message || err.message)
        });
      }
    });
  }

  getTotalUsers(): number {
    return this.groupedByUser.length;
  }

  getTotalCredentials(): number {
    return this.credentials.length;
  }
}
